import type { DeletedFrame, ExportState, MovieFrame, PlaybackSpeed, Project, ProjectFrames, SavedMovie } from '../domain/model';
import { makeId } from '../domain/model';

const DATABASE_NAME = 'little-movie-maker';
const DATABASE_VERSION = 2;
const PROJECTS = 'projects';
const FRAMES = 'frames';
export const MOVIES = 'movies';

let databasePromise: Promise<IDBDatabase> | undefined;

export class LocalStorageError extends Error {
  constructor(
    message: string,
    readonly kind: 'unavailable' | 'full' | 'corrupt' | 'unknown' = 'unknown',
  ) {
    super(message);
    this.name = 'LocalStorageError';
  }
}

function normalizeStorageError(error: unknown): LocalStorageError {
  if (error instanceof LocalStorageError) return error;
  if (error instanceof DOMException && error.name === 'QuotaExceededError') {
    return new LocalStorageError('This device is running low on storage.', 'full');
  }
  if (error instanceof Error) return new LocalStorageError(error.message);
  return new LocalStorageError('The movie could not be saved.');
}

export function requestValue<T>(request: IDBRequest): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      const result: unknown = request.result;
      resolve(result as T);
    };
    request.onerror = () => reject(normalizeStorageError(request.error));
  });
}

export function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(normalizeStorageError(transaction.error));
    transaction.onerror = () => reject(normalizeStorageError(transaction.error));
  });
}

export async function openDatabase(): Promise<IDBDatabase> {
  if (!('indexedDB' in globalThis)) {
    throw new LocalStorageError('Local movie storage is not supported on this device.', 'unavailable');
  }
  databasePromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(PROJECTS)) {
        database.createObjectStore(PROJECTS, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(FRAMES)) {
        const store = database.createObjectStore(FRAMES, { keyPath: 'id' });
        store.createIndex('projectId', 'projectId', { unique: false });
      }
      if (!database.objectStoreNames.contains(MOVIES)) {
        const store = database.createObjectStore(MOVIES, { keyPath: 'id' });
        store.createIndex('projectId', 'projectId', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
    request.onerror = () => reject(normalizeStorageError(request.error));
    request.onblocked = () => reject(new LocalStorageError('Close other copies of the app, then try again.'));
  });
  return databasePromise;
}

function freshProject(title: string): Project {
  const now = Date.now();
  return {
    id: makeId('movie'),
    title: title.trim().slice(0, 60),
    createdAt: now,
    updatedAt: now,
    frameIds: [],
    speed: 'normal',
    exportState: 'idle',
  };
}

export async function createProject(title = ''): Promise<Project> {
  const project = freshProject(title);
  const database = await openDatabase();
  const transaction = database.transaction(PROJECTS, 'readwrite');
  transaction.objectStore(PROJECTS).add(project);
  await transactionDone(transaction);
  return project;
}

export async function getProject(id: string): Promise<Project | undefined> {
  const database = await openDatabase();
  const transaction = database.transaction(PROJECTS, 'readonly');
  return requestValue<Project | undefined>(transaction.objectStore(PROJECTS).get(id));
}

export async function listProjects(): Promise<Project[]> {
  const database = await openDatabase();
  const transaction = database.transaction(PROJECTS, 'readonly');
  const projects = await requestValue<Project[]>(transaction.objectStore(PROJECTS).getAll());
  return projects.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function addFrame(
  projectId: string,
  image: Pick<MovieFrame, 'blob' | 'width' | 'height'>,
): Promise<{ project: Project; frame: MovieFrame }> {
  const database = await openDatabase();
  const transaction = database.transaction([PROJECTS, FRAMES], 'readwrite');
  const projectStore = transaction.objectStore(PROJECTS);
  const project = await requestValue<Project | undefined>(projectStore.get(projectId));
  if (!project) {
    transaction.abort();
    throw new LocalStorageError('That movie could not be found.', 'corrupt');
  }

  const now = Date.now();
  const frame: MovieFrame = {
    id: makeId('frame'),
    projectId,
    createdAt: now,
    blob: image.blob,
    width: image.width,
    height: image.height,
  };
  const updated: Project = {
    ...project,
    frameIds: [...project.frameIds, frame.id],
    updatedAt: now,
    exportState: 'idle',
  };
  transaction.objectStore(FRAMES).add(frame);
  projectStore.put(updated);
  await transactionDone(transaction);
  return { project: updated, frame };
}

export async function getFrame(frameId: string): Promise<MovieFrame | undefined> {
  const database = await openDatabase();
  const transaction = database.transaction(FRAMES, 'readonly');
  return requestValue<MovieFrame | undefined>(transaction.objectStore(FRAMES).get(frameId));
}

export async function getProjectFrames(project: Project): Promise<ProjectFrames> {
  const database = await openDatabase();
  const transaction = database.transaction(FRAMES, 'readonly');
  const store = transaction.objectStore(FRAMES);
  const loaded = await Promise.all(project.frameIds.map((id) => requestValue<MovieFrame | undefined>(store.get(id))));
  const frames = loaded.filter((frame): frame is MovieFrame => Boolean(frame));
  return { frames, missingCount: loaded.length - frames.length };
}

export async function deleteFrame(projectId: string, frameId: string): Promise<{ project: Project; deleted: DeletedFrame }> {
  const database = await openDatabase();
  const transaction = database.transaction([PROJECTS, FRAMES], 'readwrite');
  const projectStore = transaction.objectStore(PROJECTS);
  const frameStore = transaction.objectStore(FRAMES);
  const [project, frame] = await Promise.all([
    requestValue<Project | undefined>(projectStore.get(projectId)),
    requestValue<MovieFrame | undefined>(frameStore.get(frameId)),
  ]);
  if (!project || !frame) {
    transaction.abort();
    throw new LocalStorageError('That picture could not be found.', 'corrupt');
  }
  const index = project.frameIds.indexOf(frameId);
  if (index < 0) {
    transaction.abort();
    throw new LocalStorageError('That picture is not part of this movie.', 'corrupt');
  }
  const updated: Project = {
    ...project,
    frameIds: project.frameIds.filter((id) => id !== frameId),
    updatedAt: Date.now(),
    exportState: 'idle',
  };
  frameStore.delete(frameId);
  projectStore.put(updated);
  await transactionDone(transaction);
  return { project: updated, deleted: { frame, index } };
}

export async function restoreFrame(projectId: string, deleted: DeletedFrame): Promise<Project> {
  const database = await openDatabase();
  const transaction = database.transaction([PROJECTS, FRAMES], 'readwrite');
  const projectStore = transaction.objectStore(PROJECTS);
  const project = await requestValue<Project | undefined>(projectStore.get(projectId));
  if (!project) {
    transaction.abort();
    throw new LocalStorageError('That movie could not be found.', 'corrupt');
  }
  const nextIds = [...project.frameIds];
  nextIds.splice(Math.min(deleted.index, nextIds.length), 0, deleted.frame.id);
  const updated = { ...project, frameIds: nextIds, updatedAt: Date.now(), exportState: 'idle' as const };
  transaction.objectStore(FRAMES).put(deleted.frame);
  projectStore.put(updated);
  await transactionDone(transaction);
  return updated;
}

/** Swaps in the Magic Eraser result, keeping the very first original so kids can always bring back the real picture. */
export async function replaceFrameImage(projectId: string, frameId: string, blob: Blob): Promise<MovieFrame> {
  const database = await openDatabase();
  const transaction = database.transaction([PROJECTS, FRAMES], 'readwrite');
  const frameStore = transaction.objectStore(FRAMES);
  const projectStore = transaction.objectStore(PROJECTS);
  const [project, frame] = await Promise.all([
    requestValue<Project | undefined>(projectStore.get(projectId)),
    requestValue<MovieFrame | undefined>(frameStore.get(frameId)),
  ]);
  if (!project || !frame) {
    transaction.abort();
    throw new LocalStorageError('That picture could not be found.', 'corrupt');
  }
  const updated: MovieFrame = { ...frame, blob, originalBlob: frame.originalBlob ?? frame.blob };
  frameStore.put(updated);
  projectStore.put({ ...project, updatedAt: Date.now(), exportState: 'idle' as const });
  await transactionDone(transaction);
  return updated;
}

/** Brings back the untouched photo and forgets the edit. */
export async function restoreFrameImage(projectId: string, frameId: string): Promise<MovieFrame> {
  const database = await openDatabase();
  const transaction = database.transaction([PROJECTS, FRAMES], 'readwrite');
  const frameStore = transaction.objectStore(FRAMES);
  const projectStore = transaction.objectStore(PROJECTS);
  const [project, frame] = await Promise.all([
    requestValue<Project | undefined>(projectStore.get(projectId)),
    requestValue<MovieFrame | undefined>(frameStore.get(frameId)),
  ]);
  if (!project || !frame) {
    transaction.abort();
    throw new LocalStorageError('That picture could not be found.', 'corrupt');
  }
  if (!frame.originalBlob) {
    transaction.abort();
    return frame;
  }
  const updated: MovieFrame = { ...frame, blob: frame.originalBlob };
  delete updated.originalBlob;
  frameStore.put(updated);
  projectStore.put({ ...project, updatedAt: Date.now(), exportState: 'idle' as const });
  await transactionDone(transaction);
  return updated;
}

/** Moves one picture earlier or later in the movie, keeping every other picture in order. */
export async function moveFrame(projectId: string, frameId: string, offset: number): Promise<Project> {
  const database = await openDatabase();
  const transaction = database.transaction(PROJECTS, 'readwrite');
  const projectStore = transaction.objectStore(PROJECTS);
  const project = await requestValue<Project | undefined>(projectStore.get(projectId));
  if (!project) {
    transaction.abort();
    throw new LocalStorageError('That movie could not be found.', 'corrupt');
  }
  const index = project.frameIds.indexOf(frameId);
  if (index < 0) {
    transaction.abort();
    throw new LocalStorageError('That picture is not part of this movie.', 'corrupt');
  }
  const target = index + offset;
  if (!Number.isInteger(offset) || target < 0 || target >= project.frameIds.length || target === index) {
    transaction.abort();
    return project;
  }
  const nextIds = [...project.frameIds];
  nextIds.splice(target, 0, ...nextIds.splice(index, 1));
  const updated: Project = { ...project, frameIds: nextIds, updatedAt: Date.now(), exportState: 'idle' };
  projectStore.put(updated);
  await transactionDone(transaction);
  return updated;
}

export async function updateProjectSpeed(projectId: string, speed: PlaybackSpeed): Promise<Project> {
  return updateProject(projectId, { speed });
}

export async function updateExportState(projectId: string, exportState: ExportState): Promise<Project> {
  return updateProject(projectId, { exportState });
}

async function updateProject(projectId: string, patch: Partial<Pick<Project, 'speed' | 'exportState'>>): Promise<Project> {
  const database = await openDatabase();
  const transaction = database.transaction(PROJECTS, 'readwrite');
  const store = transaction.objectStore(PROJECTS);
  const project = await requestValue<Project | undefined>(store.get(projectId));
  if (!project) {
    transaction.abort();
    throw new LocalStorageError('That movie could not be found.', 'corrupt');
  }
  const updated = { ...project, ...patch, updatedAt: Date.now() };
  store.put(updated);
  await transactionDone(transaction);
  return updated;
}

export async function deleteProject(projectId: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction([PROJECTS, FRAMES, MOVIES], 'readwrite');
  const projectStore = transaction.objectStore(PROJECTS);
  const project = await requestValue<Project | undefined>(projectStore.get(projectId));
  if (project) {
    const frameStore = transaction.objectStore(FRAMES);
    project.frameIds.forEach((frameId) => frameStore.delete(frameId));
    projectStore.delete(projectId);
    // Finished movies survive project deletion as standalone snapshots.
    const movieStore = transaction.objectStore(MOVIES);
    const linked = await requestValue<SavedMovie[]>(movieStore.index('projectId').getAll(projectId));
    linked.forEach((movie) => movieStore.put({ ...movie, projectId: null }));
  }
  await transactionDone(transaction);
}

export async function resetRepositoryForTests(): Promise<void> {
  databasePromise &&= databasePromise.then((database) => {
    database.close();
    return database;
  });
  await databasePromise;
  databasePromise = undefined;
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('Test database could not be removed.'));
    request.onblocked = () => reject(new Error('Test database is blocked.'));
  });
}
